/**
 * AEOQuestionsPanel Component
 *
 * Panel for managing Q&A extracted from AEO content.
 * Shows questions with approve/reject/sync capabilities.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  HelpCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  ExternalLink,
  ArrowRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { authFetch } from '@/utils/auth-fetch';
import { cn } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';

interface AEOQuestion {
  id: string;
  question: string;
  answer: string;
  source_section: string;
  status: 'draft' | 'approved' | 'rejected' | 'synced';
  category_path: string;
  created_at: string;
  synced_at?: string;
  question_wheel_id?: string;
}

interface AEOQuestionsPanelProps {
  siteId: string;
  contentId?: string;
  categoryPath?: string;
  onSync?: (wheelId: string) => void;
  className?: string;
}

export function AEOQuestionsPanel({
  siteId,
  contentId,
  categoryPath,
  onSync,
  className,
}: AEOQuestionsPanelProps) {
  const { toast } = useToast();
  const [questions, setQuestions] = useState<AEOQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [actionLoading, setActionLoading] = useState(false);

  // PR #3269 follow-up (codex CR-1 on PR #3272): share the current scope's
  // AbortController with action-triggered reloads (handleBulkApprove /
  // handleApprove / handleReject / handleSyncToWheel post-success reloads
  // at :213/:244/:275/:321 pre-follow-up). Pre-follow-up those action-
  // reloads called `loadQuestions()` without a signal — if the user
  // switched scope AFTER the action succeeded but BEFORE the reload
  // response arrived, the stale reload would overwrite the fresh scope's
  // questions. Sharing the effect's controller via ref means the reload
  // uses the same signal that useEffect's cleanup aborts on scope change.
  const currentControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // #3269 iter-3 (codex CR-1 BLOCKING): AbortController on scope change.
    // Pre-iter-3 a late-arriving fetch from a stale scope would overwrite
    // fresh state via `setQuestions(data.data)` — reintroducing the wrong-
    // scope-action bug even after iter-2's failure-branch clear. Cancel
    // the in-flight fetch on unmount OR when siteId/categoryPath change,
    // per TOCTOU-AT-ASYNC-BOUNDARY-USE-CANCELLATION-NOT-CHECK. Cleaner
    // than a scope-check inside setQuestions (which has its own TOCTOU
    // window between check + setState).
    //
    // PR #3269 follow-up: store this controller on the shared ref so
    // action-triggered reloads (post-approve/reject/bulk/sync) can pass
    // its signal to loadQuestions. Cleanup null-guards the ref reset
    // in case a NEW effect fired before this one's cleanup (React 18
    // strict-mode double-invoke or dep-change rerun).
    const controller = new AbortController();
    currentControllerRef.current = controller;
    loadQuestions(controller.signal);
    return () => {
      controller.abort();
      if (currentControllerRef.current === controller) {
        currentControllerRef.current = null;
      }
    };
  }, [siteId, categoryPath]);

  const loadQuestions = async (signal?: AbortSignal) => {
    setLoading(true);
    try {
      let url = `/aeo-questions/sites/${siteId}`;
      if (categoryPath) {
        url += `?categoryPath=${encodeURIComponent(categoryPath)}`;
      }

      const response = await authFetch(url, { signal });
      if (response.ok) {
        const data = await response.json();
        // #3269 iter-3 belt-and-suspenders: even if fetch + json completed
        // before abort fired, don't overwrite the fresh invocation's state.
        // This closes the microtask-race window between response.json()
        // resolving and setQuestions running (in practice fetch + streaming
        // body honor the signal, but the check makes the invariant explicit).
        if (signal && signal.aborted) return;
        setQuestions(data.data || []);
        // PR #3269 follow-up (codex CR-1 on PR #3272): clear selectedIds on
        // SUCCESSFUL scope-change reload too. Pre-follow-up: iter-2 cleared
        // selection on failure branches, iter-3 aborted stale-scope success
        // paths — but a SUCCESSFUL scope-change load kept the previous
        // scope's selectedIds. User could then bulk-approve against IDs
        // that don't exist in the new scope. Per STATE-CONSISTENCY-ON-
        // SCOPE-CHANGE-COVERS-ALL-PATHS-NOT-JUST-FAILURE. selectedIds is
        // DERIVED state that doesn't auto-invalidate when questions
        // replace — must be explicitly cleared on every scope-change load.
        setSelectedIds(new Set());
      } else {
        // PR #3269 follow-up (codex CR-1 on PR #3272): mirror the success-
        // branch's iter-3 belt-and-suspenders abort check on the error
        // branch too. Pre-follow-up: A's late-arriving error (after
        // useEffect cleanup aborted A's controller due to scope change to
        // B) would still run setQuestions([]) + setSelectedIds + toast —
        // WIPING B's fresh state. Per SIBLING-WRITER-NORMALIZATION-
        // DIVERGENCE-AUDIT applied at the SUCCESS-VS-ERROR-BRANCH axis:
        // both branches must respect the signal.
        if (signal && signal.aborted) return;
        // #3153 Fix-3.5: silent-failure else-branch. Pre-#3153 a non-2xx
        // response (e.g. 401 auth expiry, 403 site permission, 500 backend
        // regression) was swallowed silently — panel stayed on stale data
        // with no user signal. Mirror the destructive-toast pattern the 4
        // action handlers use (bulkApprove, approve, reject, syncToWheel
        // from PR #3152) so loadQuestions surfaces the same class of
        // failures per the sibling-symmetry axis of the trigger-gap
        // diagnosis (docs/reports/o1/20260628-1517-question-wheel-trigger-
        // gap.md §3).
        //
        // #3269 iter-2 (codex CR-1 BLOCKING): STATE-CONSISTENCY-ON-SCOPE-
        // CHANGE. When siteId/categoryPath change and the re-fetch fails,
        // the panel would otherwise KEEP the previous scope's questions +
        // selectedIds. User could then approve/reject/sync against
        // STALE-SCOPE rows — a silent wrong-scope-action bug. Clear both
        // in the failure branches so any subsequent action fires against
        // an empty (rather than wrong-scope) set. Sister to Fix-3.5's
        // observability fix at the state-consistency axis.
        setQuestions([]);
        setSelectedIds(new Set());
        const description = await extractErrorMessage(response, 'Failed to load questions');
        // PR #3273 iter-4 (codex CR-1 BLOCKING): mirror the action-handler
        // iter-3 pattern here — extractErrorMessage() opens a new await
        // window between the iter-2 pre-extract guard and the toast. If
        // scope changes during extractErrorMessage(), the toast would
        // fire A's error under B scope. Per CANCELLATION-GUARD-AFTER-
        // EVERY-AWAIT-NOT-JUST-FIRST (founded iter-3) +
        // DISCIPLINE-APPLIES-TO-ALL-FUNCTIONS-IN-COMPONENT-NOT-JUST-
        // CODEX-FLAGGED-SITES (this iter — same discipline extends to
        // loadQuestions itself, not just action handlers).
        if (signal && signal.aborted) return;
        console.error('loadQuestions HTTP error:', response.status, description);
        toast({
          title: 'Failed to Load Questions',
          description,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      // #3269 iter-3 (codex CR-1 BLOCKING): distinguish external-abort
      // (scope change → useEffect cleanup fired) from network-layer errors.
      // On external abort, silently return: don't clear state, don't toast,
      // don't setLoading(false) — the NEW invocation (from the fresh useEffect
      // run) owns the state now. Per TOCTOU-AT-ASYNC-BOUNDARY-USE-
      // CANCELLATION-NOT-CHECK; the freshest scope's loadQuestions is the
      // authoritative writer.
      if (error && error.name === 'AbortError') {
        return;
      }
      // #3269 iter-2: mirror stale-clear in catch branch for network-layer
      // throws (offline, DNS, TLS) — same wrong-scope-action risk as
      // HTTP-error branch.
      setQuestions([]);
      setSelectedIds(new Set());
      console.error('Error loading questions:', error);
      // #3153 Fix-3.5: also surface network-layer errors (throw from
      // authFetch — offline, DNS, TLS, etc.) via toast. Prior swallow was
      // pure console-only.
      toast({
        title: 'Error Loading Questions',
        description: 'Unable to reach the server. Check your connection and retry.',
        variant: 'destructive',
      });
    } finally {
      // #3269 iter-3: skip setLoading(false) on abort — the fresh
      // invocation (from the new useEffect run) already called
      // setLoading(true) with its own signal. Flipping to false here
      // would flicker the spinner off while the fresh fetch is still
      // in flight.
      if (!signal || !signal.aborted) {
        setLoading(false);
      }
    }
  };

  const handleSelect = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const draftIds = questions
        .filter(q => q.status === 'draft')
        .map(q => q.id);
      setSelectedIds(new Set(draftIds));
    } else {
      setSelectedIds(new Set());
    }
  };

  // Extract a user-facing error message from a non-ok HTTP response. Returns the
  // backend's `error` field when present (route handlers in
  // backend-merged/routes/aeo-questions.js return `{ error: error.message }` on
  // 4xx/5xx) or a generic status-line fallback. Never throws — silent server
  // failures previously had no UI surface (this is the trigger-gap diagnosis at
  // docs/reports/o1/20260628-1517-question-wheel-trigger-gap.md §3).
  const extractErrorMessage = async (response: Response, fallback: string): Promise<string> => {
    try {
      const body = await response.clone().json();
      if (body && typeof body.error === 'string' && body.error.length > 0) {
        return body.error;
      }
    } catch {
      // body wasn't JSON; fall through
    }
    return `${fallback} (HTTP ${response.status})`;
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;

    setActionLoading(true);
    // PR #3273 iter-2 (codex CR-1 + CR-2 convergent BLOCKING): capture
    // BEFORE await. useRef guarantees IDENTITY STABILITY (same wrapper
    // object across renders) but NOT VALUE STABILITY (contents mutate
    // via useEffect cleanup on scope change). Per REF-BASED-CANCELLATION-
    // CAPTURE-BEFORE-AWAIT-NOT-AFTER + [[USEREF-IDENTITY-STABILITY-VS-
    // VALUE-STABILITY]].
    const capturedController = currentControllerRef.current;
    // PR #3273 iter-3 (codex CR-3+CR-4 convergent BLOCKING): guard after
    // EVERY await, not just the first mutation-request. Multiple await
    // windows in each handler (authFetch, response.json,
    // extractErrorMessage, catch block) each open a race window where
    // useEffect cleanup can swap the ref. Per CANCELLATION-GUARD-AFTER-
    // EVERY-AWAIT-NOT-JUST-FIRST — sub-discipline of my iter-3
    // CANCELLATION-PLUS-BELT-CHECK at the MULTI-AWAIT-COVERAGE axis.
    // Helper closes over capturedController + currentControllerRef;
    // reused at every guard site to keep the check semantics identical.
    const isStaleScope = () =>
      !capturedController
      || capturedController.signal.aborted
      || currentControllerRef.current !== capturedController;

    try {
      const response = await authFetch('/aeo-questions/bulk-approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questionIds: Array.from(selectedIds) }),
      });

      // GUARD 1 (iter-2): post-authFetch.
      if (isStaleScope()) return;

      if (response.ok) {
        const data = await response.json();
        // GUARD 2 (iter-3): post-response.json() — scope may have changed
        // during body streaming/parsing.
        if (isStaleScope()) return;
        toast({
          title: 'Questions Approved',
          description: `${data.data.approvedCount} questions approved for sync`,
        });
        setSelectedIds(new Set());
        // Pass CAPTURED signal (not ref-deref) so the reload's abort tie
        // stays consistent with this handler's scope even if ref gets
        // swapped between the guard and the reload call.
        loadQuestions(capturedController!.signal);
      } else {
        const description = await extractErrorMessage(response, 'Failed to approve questions');
        // GUARD 3 (iter-3): post-extractErrorMessage on error branch.
        if (isStaleScope()) return;
        console.error('Bulk-approve HTTP error:', response.status, description);
        toast({
          title: 'Bulk Approve Failed',
          description,
          variant: 'destructive',
        });
      }
    } catch (error) {
      // GUARD 4 (iter-3): catch block. Under aborted/stale scope,
      // silently swallow — the error is stale + fresh scope's own
      // fetch/action will surface any real errors.
      if (isStaleScope()) return;
      console.error('Error approving questions:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve questions',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async (questionId: string) => {
    setActionLoading(true);
    // PR #3273 iter-2/iter-3: capture-BEFORE-await + guard-after-every-
    // await. See handleBulkApprove for the full rationale + methodology
    // references.
    const capturedController = currentControllerRef.current;
    const isStaleScope = () =>
      !capturedController
      || capturedController.signal.aborted
      || currentControllerRef.current !== capturedController;

    try {
      const response = await authFetch(`/aeo-questions/${questionId}/approve`, {
        method: 'PUT',
      });
      if (isStaleScope()) return;

      if (response.ok) {
        toast({ title: 'Question Approved' });
        loadQuestions(capturedController!.signal);
      } else {
        const description = await extractErrorMessage(response, 'Failed to approve question');
        if (isStaleScope()) return;
        console.error('Approve HTTP error:', response.status, description);
        toast({
          title: 'Approve Failed',
          description,
          variant: 'destructive',
        });
      }
    } catch (error) {
      if (isStaleScope()) return;
      console.error('Error approving question:', error);
      toast({
        title: 'Approve Failed',
        description: 'Failed to approve question',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (questionId: string) => {
    setActionLoading(true);
    // PR #3273 iter-2/iter-3: capture-BEFORE-await + guard-after-every-
    // await. Mirror handleApprove.
    const capturedController = currentControllerRef.current;
    const isStaleScope = () =>
      !capturedController
      || capturedController.signal.aborted
      || currentControllerRef.current !== capturedController;

    try {
      const response = await authFetch(`/aeo-questions/${questionId}/reject`, {
        method: 'PUT',
      });
      if (isStaleScope()) return;

      if (response.ok) {
        toast({ title: 'Question Rejected' });
        loadQuestions(capturedController!.signal);
      } else {
        const description = await extractErrorMessage(response, 'Failed to reject question');
        if (isStaleScope()) return;
        console.error('Reject HTTP error:', response.status, description);
        toast({
          title: 'Reject Failed',
          description,
          variant: 'destructive',
        });
      }
    } catch (error) {
      if (isStaleScope()) return;
      console.error('Error rejecting question:', error);
      toast({
        title: 'Reject Failed',
        description: 'Failed to reject question',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSyncToWheel = async () => {
    if (!categoryPath) {
      toast({
        title: 'Error',
        description: 'Category path is required for sync',
        variant: 'destructive',
      });
      return;
    }

    setActionLoading(true);
    // PR #3273 iter-2/iter-3: capture-BEFORE-await + guard-after-every-
    // await. Additionally: skip the onSync parent callback under stale
    // scope (wheelId belongs to OLD scope; parent shouldn't navigate
    // fresh-scope UI to old wheel).
    const capturedController = currentControllerRef.current;
    const isStaleScope = () =>
      !capturedController
      || capturedController.signal.aborted
      || currentControllerRef.current !== capturedController;

    try {
      const response = await authFetch(`/aeo-questions/sync/${siteId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categoryPath }),
      });
      if (isStaleScope()) return;

      if (response.ok) {
        const data = await response.json();
        if (isStaleScope()) return;
        toast({
          title: 'Synced to Question Wheel',
          description: `${data.data.syncedCount} questions synced`,
        });
        loadQuestions(capturedController!.signal);
        if (data.data.wheelId && onSync) {
          onSync(data.data.wheelId);
        }
      } else {
        const description = await extractErrorMessage(response, 'Failed to sync questions');
        if (isStaleScope()) return;
        console.error('Sync HTTP error:', response.status, description);
        toast({
          title: 'Sync Failed',
          description,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      if (isStaleScope()) return;
      console.error('Error syncing to wheel:', error);
      toast({
        title: 'Sync Failed',
        description: error.message || 'Failed to sync questions',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const statusCounts = {
    draft: questions.filter(q => q.status === 'draft').length,
    approved: questions.filter(q => q.status === 'approved').length,
    synced: questions.filter(q => q.status === 'synced').length,
    rejected: questions.filter(q => q.status === 'rejected').length,
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <Badge variant="secondary">Draft</Badge>;
      case 'approved':
        return <Badge variant="default" className="bg-amber-500">Ready</Badge>;
      case 'synced':
        return <Badge variant="default" className="bg-green-600">Synced</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">Loading questions...</div>
        </CardContent>
      </Card>
    );
  }

  if (questions.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">
            No questions extracted yet. Save the AEO content to extract FAQs.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <HelpCircle className="h-4 w-4" />
              AEO Questions
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              {questions.length} questions extracted from FAQs
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-500">{statusCounts.draft} draft</span>
            <span className="text-amber-500">{statusCounts.approved} ready</span>
            <span className="text-green-600">{statusCounts.synced} synced</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Action Bar */}
        <div className="flex items-center justify-between mb-4 pb-3 border-b">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={selectedIds.size > 0 && selectedIds.size === statusCounts.draft}
              onCheckedChange={handleSelectAll}
              disabled={statusCounts.draft === 0}
            />
            <span className="text-sm text-muted-foreground">
              {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all draft'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleBulkApprove}
                disabled={actionLoading}
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Approve Selected
              </Button>
            )}
            {statusCounts.approved > 0 && (
              <Button
                size="sm"
                onClick={handleSyncToWheel}
                disabled={actionLoading}
              >
                <ArrowRight className="h-4 w-4 mr-1" />
                Sync to Wheel ({statusCounts.approved})
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              // PR #3269 follow-up: the Refresh button previously passed a
              // click event as the first argument (which happens to type-
              // narrow to `undefined` at runtime since MouseEvent isn't an
              // AbortSignal, but is technically wrong). Wrap so we
              // explicitly pass the current scope's signal — a scope
              // change during a user-triggered refresh will now abort the
              // stale reload cleanly.
              onClick={() => loadQuestions(currentControllerRef.current?.signal)}
              disabled={loading}
            >
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            </Button>
          </div>
        </div>

        {/* Questions List */}
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {questions.map((q) => (
            <div
              key={q.id}
              className={cn(
                'p-3 rounded-lg border',
                q.status === 'synced' && 'bg-green-50 border-green-200',
                q.status === 'approved' && 'bg-amber-50 border-amber-200',
                q.status === 'rejected' && 'bg-red-50 border-red-200 opacity-60',
                q.status === 'draft' && 'bg-muted/30'
              )}
            >
              <div className="flex items-start gap-3">
                {q.status === 'draft' && (
                  <Checkbox
                    checked={selectedIds.has(q.id)}
                    onCheckedChange={(checked) => handleSelect(q.id, checked as boolean)}
                    className="mt-1"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {getStatusBadge(q.status)}
                    <span className="text-xs text-muted-foreground">
                      {q.source_section}
                    </span>
                  </div>
                  <p className="font-medium text-sm">{q.question}</p>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                    {q.answer}
                  </p>
                </div>
                {q.status === 'draft' && (
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-green-600 hover:bg-green-100"
                      onClick={() => handleApprove(q.id)}
                      disabled={actionLoading}
                    >
                      <CheckCircle className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-red-600 hover:bg-red-100"
                      onClick={() => handleReject(q.id)}
                      disabled={actionLoading}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                {q.status === 'synced' && q.question_wheel_id && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 text-xs"
                    asChild
                  >
                    <a href={`/question-wheels/${q.question_wheel_id}/debug`}>
                      <ExternalLink className="h-3 w-3 mr-1" />
                      View Wheel
                    </a>
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default AEOQuestionsPanel;
